import { useAuth } from '../core/AuthContext'
import { useThemeStore } from '../stores/themeStore'
import { useNavigate } from 'react-router-dom'
import Icon from '../components/Icon'

export default function SettingsPage() {
  const { user, logout } = useAuth()
  const { theme, toggleTheme } = useThemeStore()
  const navigate = useNavigate()

  const initials = (user?.name || 'U')
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

  return (
    <div className="px-4 md:px-10 py-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <h1 className="text-headline-lg-mobile md:text-headline-lg">Settings</h1>

        {/* Profile card */}
        <section className="bg-surface border border-border rounded-xl p-5 flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-accent/15 border border-accent/30 flex items-center justify-center shrink-0">
            <span className="text-headline-md text-accent">{initials}</span>
          </div>
          <div className="min-w-0">
            <p className="text-label-md text-text-primary truncate">{user?.name || 'Unknown'}</p>
            <p className="text-body-md text-text-secondary truncate">{user?.email}</p>
          </div>
        </section>

        {/* Appearance */}
        <section className="bg-surface border border-border rounded-xl divide-y divide-border">
          <SettingRow
            icon={theme === 'dark' ? 'dark_mode' : 'light_mode'}
            title="Appearance"
            subtitle={theme === 'dark' ? 'Dark mode' : 'Light mode'}
          >
            <button
              onClick={toggleTheme}
              role="switch"
              aria-checked={theme === 'dark'}
              className={`relative w-12 h-7 min-h-0 rounded-full transition-colors ${
                theme === 'dark' ? 'bg-accent' : 'bg-surface-bright'
              }`}
            >
              <span
                className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-all ${
                  theme === 'dark' ? 'left-[22px]' : 'left-0.5'
                }`}
              />
            </button>
          </SettingRow>
        </section>

        {/* Security */}
        <section className="bg-surface border border-border rounded-xl divide-y divide-border">
          <button
            onClick={() => navigate('/settings/change-password')}
            className="w-full text-left hover:bg-surface-high transition"
          >
            <SettingRow
              icon="lock"
              title="Change Password"
              subtitle="Update your password regularly"
            >
              <Icon name="arrow_forward" className="text-text-muted text-lg" />
            </SettingRow>
          </button>
        </section>

        {/* About */}
        <section className="bg-surface border border-border rounded-xl divide-y divide-border">
          <SettingRow icon="info" title="Version" subtitle="Snagmatic v0.1.0" />
          <SettingRow
            icon="volunteer_activism"
            title="About"
            subtitle="Help your community report infrastructure issues."
          />
        </section>

        {/* Sign out */}
        <button
          onClick={logout}
          className="w-full flex items-center justify-center gap-2 border border-status-urgent/40 text-status-urgent text-label-md rounded-xl py-3.5 hover:bg-status-urgent/10 transition"
        >
          <Icon name="logout" className="text-xl" />
          Sign Out
        </button>
      </div>
    </div>
  )
}

function SettingRow({ icon, title, subtitle, children }) {
  return (
    <div className="flex items-center gap-4 px-4 py-4">
      <div className="bg-surface-high p-2.5 rounded-xl shrink-0">
        <Icon name={icon} className="text-accent text-xl" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-label-md text-text-primary">{title}</p>
        {subtitle && <p className="text-label-sm text-text-muted truncate">{subtitle}</p>}
      </div>
      {children}
    </div>
  )
}
